import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { fetchScreenshotUrl, resolveReport } from "./problemReports";

/*
 * The small "Problem report #PR-… · Won't load · Appointments" chip on a patient chat message that
 * is a Report-a-Problem report (patient app, 10-23): screenshot thumbnail (click = full size in a
 * modal; every view is audited by the server), and Mark resolved (posts the normal "marked fixed"
 * reply and pushes the patient). Both chat copies render it.
 */
export default function ProblemReportChip({ report, onResolved }) {
  const [thumb, setThumb] = useState(null);
  const [full, setFull] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const hasShot = !!(report && report.screenshot);
  const resolved = report && report.status === "resolved";
  const reportId = report && report.report_id;

  useEffect(() => {
    if (!hasShot || !reportId) return undefined;
    let alive = true;
    let url = null;
    fetchScreenshotUrl(reportId, "thumb")
      .then((u) => { url = u; if (alive) setThumb(u); else URL.revokeObjectURL(u); })
      .catch(() => {});
    return () => { alive = false; if (url) URL.revokeObjectURL(url); };
  }, [hasShot, reportId]);

  useEffect(() => () => { if (full) URL.revokeObjectURL(full); }, [full]);

  if (!report) return null;
  const label = [`Problem report #${report.reference}`, report.category_label, report.screen_name]
    .filter(Boolean).join(" · ");

  const openFull = async () => {
    setOpen(true);
    if (full) return;
    try {
      setFull(await fetchScreenshotUrl(reportId, "full"));
    } catch (e) {
      setError("Could not load the screenshot");
    }
  };

  const resolve = async () => {
    setBusy(true);
    setError("");
    try {
      const body = await resolveReport(reportId);
      if (onResolved) onResolved(reportId, { resolved_at: body?.resolved_at, resolved_by: body?.resolved_by });
    } catch (e) {
      const code = e && e.response && e.response.status === 409 ? e.response.data?.error?.code : null;
      if (code === "ALREADY_RESOLVED") {
        if (onResolved) onResolved(reportId, {});          // someone else resolved it first
      } else {
        setError("Could not mark resolved");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="problem-report-chip"
      className="mb-1 rounded-lg bg-white text-[#1C1D22] px-2 py-1 text-[12px] font-font1 flex flex-col gap-1"
    >
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-medium">{label}</span>
        <span
          className={`rounded-full px-2 text-[11px] ${resolved ? "bg-[#E6F4EA] text-[#1E7B34]" : "bg-[#FDEBD0] text-[#9A5B00]"}`}
        >
          {resolved ? "Resolved" : "Open"}
        </span>
      </div>
      <div className="flex items-end gap-2">
        {hasShot && (
          thumb ? (
            <button type="button" onClick={openFull} className="p-0 border-0 bg-transparent cursor-pointer"
              title="View screenshot">
              <img src={thumb} alt={`Screenshot for ${report.reference}`} className="max-h-24 rounded border" />
            </button>
          ) : (
            <span className="text-[11px] text-gray-500">Screenshot…</span>
          )
        )}
        {!resolved && (
          <button
            type="button"
            onClick={resolve}
            disabled={busy}
            className="ml-auto rounded border border-[#193EB9] text-[#193EB9] px-2 py-[2px] text-[12px] disabled:opacity-50"
          >
            {busy ? "Saving…" : "Mark resolved"}
          </button>
        )}
      </div>
      {error && <span className="text-[11px] text-red-600">{error}</span>}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md">
        <div className="flex justify-between items-center px-3 pt-2">
          <span className="text-[14px] font-medium">{label}</span>
          <IconButton aria-label="Close" size="small" onClick={() => setOpen(false)}><ClearIcon /></IconButton>
        </div>
        <DialogContent>
          {full ? (
            <img src={full} alt={`Screenshot for ${report.reference}`} style={{ maxWidth: "100%", maxHeight: "80vh" }} />
          ) : (
            <span className="text-[13px]">{error || "Loading…"}</span>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
